/* eslint-disable max-len */
/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * List of apis
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Misha Kokoulin
 * @author Maxim Fedorov
 * @author Julia Ignatieva
 * @author Dmitriy Kvon
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.0.0
 */
import { stringFormat } from '@utils/stringFormat';

import { EWizardName } from '../wizard/wizardConstants';

export const API_HOSTNAME = '/ws';
export const API_VERSION_DEFAULT = 'v1.0';

//roaming options
export const ROAMING = '/roaming';
export const COUNTRIES = '/countries';
export const OPTIONS = '/options';

//app
export const APP_CONFIG = `/${API_VERSION_DEFAULT}/app/config`;
export const BALANCE_SYNC = `/${API_VERSION_DEFAULT}/balance/sync`;
export const USER_TIMEZONE = `/${API_VERSION_DEFAULT}/i18n/timezone`;

// zk internalization
export const API_INTERNATIONALIZATION_FROM_ZK = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/i18n/common`;

//auth
export const USER_AUTH = `/${API_VERSION_DEFAULT}/user/auth`;
export const USER_SETTINGS = `/${API_VERSION_DEFAULT}/settings/user`;
export const IS_USER_CACHED = `/${API_VERSION_DEFAULT}/user/cache/status`;
export const LOGIN = `/${API_VERSION_DEFAULT}/auth/process`;
// логин пользователя как СП
export const LOGIN_SERVICE_ACCESS = `/${API_VERSION_DEFAULT}/auth/service-access/process`;
export const API_SERVICE_ACCESS_CHANGE_USER = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/user/auth/service-access/change-user`;
// вход СП под абонентом
export const LOGIN_USER_SERVICE_ACCESS = `/${API_VERSION_DEFAULT}/auth/service-access/user`;
export const RECOVERY_LOGIN = `/${API_VERSION_DEFAULT}/user/password/recovery`;
export const TEMPORARY_PASSWORD = `/${API_VERSION_DEFAULT}/user/password/temporary`;
export const CAPTCHA = `/${API_VERSION_DEFAULT}/captcha/image`;
export const CAPTCHA_CHECK = `/${API_VERSION_DEFAULT}/captcha/check`;
export const API_LOGOUT = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/user/auth/logout`;
export const API_NOTIFICATION = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/notifier`;
export const API_NOTIFICATION_ACCOUNT = `${API_NOTIFICATION}/account/{clientId}`;
export const API_NOTIFICATION_SUBSCRIBER = `${API_NOTIFICATION}/subscriber/{subscriberId}`;

// pages
const ACCOUNT = 'account';
const INFO = 'info';
export const ACCOUNT_INFO = `${ACCOUNT}/${INFO}`;
const ADD = 'add';
export const CANCEL = 'cancel';
export const UPDATE = 'update';
export const UPDATE_ALL = 'updateAll';
export const DELETE = 'delete';
export const SHOWN = 'shown';
export const REPORTS = 'reports';
export const VIEWED = 'viewed';
const SUBSCRIBER = 'subscriber';
const MOBILE = 'mobile';
const LIST = 'list';
const STATUS = 'status';
const META = 'meta';
const DASHBOARD = 'dashboard';
const CONTAINS = 'contains';
const COUNT = 'count';
const NEWS = 'news';
const ARCHIVE = 'archive';
const MANAGE = 'manage';
const PUBLISH = 'publish';
const EXTERNAL = 'external';
const INTERNAL = 'internal';
const MEGA_POWERS = 'mega-powers';
const FEEDBACK = 'feedback';

export const BILLS = `/${API_VERSION_DEFAULT}/bills`;
export const BILLS_ACCOUNT = `/${API_VERSION_DEFAULT}/bills/account/{0}`;
export const BILLS_ACCOUNT_PROXY = `${BILLS}/{0}`;
export const BILLS_UNPAID = `/${API_VERSION_DEFAULT}/bills/unpaid`;
export const BILLS_UNPAID_ACCOUNT = `/${API_VERSION_DEFAULT}/bills/unpaid/account/{0}`;
export const BILLS_UNPAID_ACCOUNT_PROXY = `${BILLS_UNPAID}/{0}`;
export const EXPENSES_ANALYZER = `/${API_VERSION_DEFAULT}/expenses`;
export const EXPENSES_ANALYZER_CHART = `${API_HOSTNAME}${EXPENSES_ANALYZER}/graph`;
export const EXPENSE_ANALYZER_CHART_ACCOUNT = `${EXPENSES_ANALYZER_CHART}/account?from={0}&to={1}`;
export const EXPENSE_ANALYZER_CHART_SUBSCRIBER = `${EXPENSES_ANALYZER_CHART}/subscriber?from={0}&to={1}`;
export const EXPENSE_ANALYZER_CHART_BILLS_ACCOUNT = `${EXPENSES_ANALYZER_CHART}/bills/account`;
export const EXPENSE_ANALYZER_CHART_BILLS_SUBSCRIBER = `${EXPENSES_ANALYZER_CHART}/bills/subscriber`;
export const EXPENSE_ANALYZER_CHART_ACCOUNT_DETAILS = `${API_HOSTNAME}${EXPENSES_ANALYZER}/account/from/account/details?from={0}&to={1}`;
export const EXPENSE_ANALYZER_CHART_FIXED_DETAILS = `${API_HOSTNAME}${EXPENSES_ANALYZER}/subscriber/from/fixed/details?from={0}&to={1}`;
export const EXPENSE_ANALYZER_CHART_MOBILE_DETAILS = `${API_HOSTNAME}${EXPENSES_ANALYZER}/subscriber/from/mobile/details?from={0}&to={1}`;
export const EXPENSE_ANALYZER_CHART_SUBSCRIBER_DETAILS = `${API_HOSTNAME}${EXPENSES_ANALYZER}/subscriber/from/account/details?from={0}&to={1}`;
export const EXPENSE_ANALYZER_CHART_BILLS_ACCOUNT_DETAILS = `${API_HOSTNAME}${EXPENSES_ANALYZER}/account/from/bills/details?from={0}&to={1}`;
export const EXPENSE_ANALYZER_CHART_BILLS_SUBSCRIBER_DETAILS = `${API_HOSTNAME}${EXPENSES_ANALYZER}/subscriber/from/bills/details?from={0}&to={1}`;

export const EXPORT_ACCOUNT = `/${API_VERSION_DEFAULT}/account/export/{0}`;
export const EXPORT_BILLS = '/export/{0}';
export const EXPORT_FIXED_SUBSCRIBER = `/${API_VERSION_DEFAULT}/subscriber/fixed/export/{0}`;
export const EXPORT_INQUIRY = `/${API_VERSION_DEFAULT}/inquiry/export/{0}`;
export const EXPORT_MOBILE_SUBSCRIBER = `/${API_VERSION_DEFAULT}/subscriber/mobile/export/{0}`;
export const EXPORT_ORDER = `/${API_VERSION_DEFAULT}/orders/export/{0}`;
export const EXPORT_SETTINGS_STATISTICS = `/${API_VERSION_DEFAULT}/statistics/export/{0}`;

export const FIELD_SUGGEST = '/suggest';
export const INQUIRIES = `/${API_VERSION_DEFAULT}/inquiry`;
export const INQUIRY_SYNC = INQUIRIES + '/sync';
export const INQUIRY_STATE = INQUIRIES + '/state';

export const PERSONAL_ACCOUNT = `/${API_VERSION_DEFAULT}/account`;
export const SUBSCRIBER_FIXED = `/${API_VERSION_DEFAULT}/subscriber/fixed`;
export const SUBSCRIBER_MOBILE = `/${API_VERSION_DEFAULT}/subscriber/mobile`;
export const SUBSCRIBER_MOBILE_API = '/subscriber/mobile';
export const API_CAMPAIGN_INFO_ACCOUNT = `/${API_VERSION_DEFAULT}/campaign/client/{campaignId}`;
export const API_CAMPAIGN_INFO_ACCOUNT_LIST = `/${API_VERSION_DEFAULT}/campaign/client/{campaignId}`;
export const API_CAMPAIGN_INFO_SUBSCRIBER = `/${API_VERSION_DEFAULT}/campaign/subscriber/{campaignId}`;
export const API_CAMPAIGN_INFO_SUBSCRIBER_LIST = `/${API_VERSION_DEFAULT}/campaign/subscriber/{campaignId}`;
export const API_CAMPAIGNS_ACCOUNT = `/${API_VERSION_DEFAULT}/campaigns/account`;
export const API_CAMPAIGNS_SUBSCRIBER = `/${API_VERSION_DEFAULT}/campaigns/subscriber`;

export const SUBSCRIBER_MOBILE_GET_TABLE_STATUS = `${API_HOSTNAME}${SUBSCRIBER_MOBILE}/list/status`;
export const SUBSCRIBER_MOBILE_REGIONS = `${SUBSCRIBER_MOBILE}/regions`;
export const SUBSCRIBER_MOBILE_REGIONS_BASKET = `${SUBSCRIBER_MOBILE}/regions/basket/{basketType}`;
export const SUBSCRIBER_MOBILE_REGIONS_SUBSCRIBERS = `${SUBSCRIBER_MOBILE}/regions/subscribers`;

export const SUBSCRIBER_MOBILE_EXTFILTER = `/${API_VERSION_DEFAULT}/subscriber/mobile/extfilter`;
export const SUBSCRIBER_MOBILE_EXTFILTER_CLEAR = `/${API_VERSION_DEFAULT}/subscriber/mobile/extfilter/clear`;
export const SUBSCRIBER_MOBILE_EXTFILTER_LIST = `/${API_VERSION_DEFAULT}/subscriber/mobile/extfilter/list`;
export const SUBSCRIBER_MOBILE_EXTFILTER_META = `/${API_VERSION_DEFAULT}/subscriber/mobile/extfilter/meta`;
export const SUBSCRIBER_MOBILE_EXTFILTER_SAVE = `/${API_VERSION_DEFAULT}/subscriber/mobile/extfilter/add`;
export const SUBSCRIBER_MOBILE_EXTFILTER_UPDATE = `/${API_VERSION_DEFAULT}/subscriber/mobile/extfilter/update`;
export const TABLE_LIST = '/list';
export const TABLE_META = '/meta';

export const API_PERSONAL_ACCOUNT_LIST = `${PERSONAL_ACCOUNT}${TABLE_LIST}`;

// Labels
export const LABEL_META = '/labels';

//banners
export const LOAD_BANNERS = `/${API_VERSION_DEFAULT}/campaign/banners`;
export const HIDE_BANNERS = (id: number) => `/${API_VERSION_DEFAULT}/campaign/banners/${id}/hide`;

// fields
export const FIELDS = '/fields';
export const FIELDS_SIZE = '/fields/resize';

//basket
export const BASKET = '/basket';
export const BASKET_COUNT = '/count';
export const BASKET_LIST = '/list';
export const RESET_BASKET = '/state';

export const API_CAMPAIGN_INFO_BASKET = `/${API_VERSION_DEFAULT}/campaign${BASKET}/{campaignId}`;
export const API_CAMPAIGN_SUBSCRIBER_INFO_BASKET = `/${API_VERSION_DEFAULT}/campaign/subscriber/{campaignId}${BASKET}`;
export const API_CAMPAIGN_ACCOUNT_INFO_BASKET = `/${API_VERSION_DEFAULT}/campaign/account/{campaignId}${BASKET}`;
export const BILLS_BASKET = BILLS + BASKET;
export const BILLS_BASKET_ACCOUNT = `${BILLS_BASKET}/{0}`;
export const BILLS_UNPAID_BASKET = BILLS_UNPAID + BASKET;
export const BILLS_UNPAID_BASKET_ACCOUNT = `${BILLS_UNPAID_BASKET}/{0}`;
export const EXPENSES_ANALYZER_ACCOUNTS_BASKET = `${EXPENSES_ANALYZER}/account/basket`;
export const EXPENSES_ANALYZER_ACCOUNTS_LINE = `${EXPENSES_ANALYZER}/account/from/{0}/line`;
export const EXPENSES_ANALYZER_BASKET = `${EXPENSES_ANALYZER}/basket`;
export const EXPENSES_ANALYZER_BILLS_ACCOUNT_BASKET = EXPENSES_ANALYZER + '/account' + BASKET;
export const EXPENSES_ANALYZER_BILLS_SUBSCRIBER_BASKET = EXPENSES_ANALYZER + '/subscriber' + BASKET;
export const EXPENSES_ANALYZER_SUBSCRIBER_BASKET = EXPENSES_ANALYZER + '/subscriber' + BASKET;
export const EXPENSES_ANALYZER_SUBSCRIBER_FIXED_BASKET = EXPENSES_ANALYZER + '/subscriber' + BASKET;
export const EXPENSES_ANALYZER_SUBSCRIBER_MOBILE_BASKET = EXPENSES_ANALYZER + '/subscriber' + BASKET;
export const EXPENSES_ANALYZER_SUBSCRIBERS_BASKET = EXPENSES_ANALYZER + '/subscriber' + BASKET;
export const EXPENSES_ANALYZER_TABLE_DETAILS_ACCOUNT_BILLS = `${EXPENSES_ANALYZER}/account/bills/details`;
export const EXPENSES_ANALYZER_TABLE_DETAILS_ACCOUNT = `${EXPENSES_ANALYZER}/account/details`;
export const EXPENSES_ANALYZER_TABLE_DETAILS_SUBSCRIBER_BILLS = `${EXPENSES_ANALYZER}/subscriber/bills/details`;
export const EXPENSES_ANALYZER_TABLE_DETAILS_SUBSCRIBER = `${EXPENSES_ANALYZER}/subscriber/details`;
export const INQUIRIES_BASKET = INQUIRIES + BASKET;
export const PERSONAL_ACCOUNT_BASKET = PERSONAL_ACCOUNT + BASKET;
export const SUBSCRIBER_FIXED_BASKET = SUBSCRIBER_FIXED + BASKET;
export const SUBSCRIBER_MOBILE_BASKET = SUBSCRIBER_MOBILE + BASKET;
export const ADD_CHECKBOX = '/add';
export const DELETE_CHECKBOX = '/delete';

//header
export const SUPER_SEARCH = `/${API_VERSION_DEFAULT}/search`;

//logging
export const BUSINESS_LOGGING = `/${API_VERSION_DEFAULT}/logger/business`;

//filter
export const FILTER_AND_SORT = '/filter-and-sort';
export const FILTER_AND_SORT_PRESET = '/filter-and-sort-preset?filterId={filterId}';

//search
export const FILTER_SEARCH = '/suggest';
//search
export const SEARCH_BILLS_NUMBER = '/suggest/bill-number';

// Table footer
export const BASKET_ACCOUNT_ADV_PAY = (sum: string) => `/${API_VERSION_DEFAULT}/account/adv_pay?sum=${sum}`;
export const CHECK_ACCESS_LEVEL = '/check-access-level';
export const INQUIRY_MODAL = `/${API_VERSION_DEFAULT}/inquiry`;
export const INQUIRY_TOPIC = '/topic';
export const INQUIRY_USER = '/user';
export const INQUIRY_FILE = '/attachment';
export const EDIT_LABEL = '/labels';
export const EDIT_LABEL_SUBSCRIBER_ID = '/label';
export const LABEL_EDITOR = '/labels/basket';
export const LABEL_EDITOR_SUBSCRIBER_ID = '/labels?entityType={entityType}';
export const LABEL_CURRENT = '/labels/basket/current';
export const LABEL_CURRENT_SUBSCRIBER_ID = '/labels/current';
export const ZONES_BASKET = '/zones/basket';

// Widgets
export const REPORT_ACCOUNT_ADV_PAY = (clientId: string, sum: string) =>
    `/${API_VERSION_DEFAULT}/account/adv_pay/${clientId}?sum=${sum}`;
export const WIDGET_ACCOUNT_BALANCE = '/accounts/balance';
export const WIDGET = '/widget'
export const WIDGET_API = `/${API_VERSION_DEFAULT}${WIDGET}`;
export const WIDGET_COSTS = `${WIDGET_API}/expenses/{accountId}?from={from}&to={to}`;
export const WIDGET_MAX_COSTS = '/subs-spent-most';
export const WIDGET_META = `/${API_VERSION_DEFAULT}/dashboard/meta`;
export const WIDGET_MULTI_BALANCES = '/multi-balances';
export const WIDGET_ORDER_INQUIRIES_INQUIRIES = '/history/inquiries';
export const WIDGET_ORDER_INQUIRIES_ORDERS = '/history/orders';
export const WIDGET_PAYMENTS = '/payments';
export const WIDGET_SEARCH_ACCOUNTS = '/accounts/search';
export const WIDGET_VOTING_SURVEY = '/survey';
export const WIDGET_EXPENSES_LIST_STATE = (bisAccountId: string) => WIDGET_API + `/expenses/state/${bisAccountId}`;

export const WIDGET_SUBSCRIBER_MOBILE = '/subscribers/mobile';

// Fast actions
export const FAST_ACTIONS = `${WIDGET}/actions/general`;
export const SUBSCRIBERS_IN_ROAMING_API = `${SUBSCRIBER_MOBILE_API}/subscribers-in-roaming`;

//Expenses
export const EXPENSES_ACCOUNT = `/${API_VERSION_DEFAULT}/expenses/account`;
export const EXPENSES_SUBSCRIBER = `/${API_VERSION_DEFAULT}/expenses/subscriber`;

// Wizard
export const WIZARD = `/${API_VERSION_DEFAULT}/wizard/{0}`;
export const WIZARD_START_FROM = '/start/from/{0}';
export const WIZARD_META = '/meta';
export const WIZARD_ACTION = '/action/{0}';
export const WIZARD_NEXT = '/next';
export const WIZARD_BACK = '/back';
export const WIZARD_FINISH = '/finish';

export const LABELS_IMPORT_UPLOAD = 'upload/upload-labels';
export const LABELS_IMPORT_VALIDATE = 'validate-labels';
export const LABELS_IMPORT_VALIDATE_STATUS = 'validate-labels-status';
export const LABELS_IMPORT_EXPORT_ERRORS = 'download/export-errors-labels';
export const LABELS_IMPORT_VALIDATED_LIST = 'validated-labels-list';

export const PRODUCT_OFFERINGS_CREATE_INQUIRY =
    stringFormat(WIZARD, [EWizardName.PRODUCT_OFFERINGS_CAMPAIGN]) + stringFormat(WIZARD_ACTION, ['send-inquiry']);
export const PRODUCT_OFFERINGS_GET_SUBSCRIBERS = 'subscribers';
export const PRODUCT_OFFERINGS_GET_MEGA_POWERS_LIST = `${MEGA_POWERS}/list`;
export const PRODUCT_OFFERINGS_UPDATE_MEGA_POWERS = `${MEGA_POWERS}/update`;
export const PRODUCT_OFFERINGS_SUBSCRIBERS_BASKET =
    stringFormat(WIZARD, [EWizardName.PRODUCT_OFFERINGS]) + stringFormat(WIZARD_ACTION, ['subscribers']);
export const PRODUCT_OFFERINGS_SUBSCRIBERS_CHECK = PRODUCT_OFFERINGS_SUBSCRIBERS_BASKET + '/check';
export const PRODUCT_OFFERINGS_CAMPAIGN_SUBSCRIBERS_BASKET =
    stringFormat(WIZARD, [EWizardName.PRODUCT_OFFERINGS_CAMPAIGN]) + stringFormat(WIZARD_ACTION, ['subscribers']);
export const PRODUCT_OFFERINGS_CAMPAIGN_SUBSCRIBERS_CHECK = PRODUCT_OFFERINGS_CAMPAIGN_SUBSCRIBERS_BASKET + '/check';

export const IMPORT_PERSONAL_DATA_UPLOAD_DOCUMENT = 'upload-document';
export const IMPORT_PERSONAL_DATA_LIST = 'list';
export const IMPORT_PERSONAL_DATA_EXPORT_ERROR = 'export-errors';

export const IMPORT_SUBSCRIBERS_LIMITS_UPLOAD_DOCUMENT = 'upload-document';
export const IMPORT_SUBSCRIBERS_LIMITS_LIST = 'list';
export const IMPORT_SUBSCRIBERS_LIMITS_EXPORT_ERROR = 'export-errors';

// Settings
export const SETTINGS = `/${API_VERSION_DEFAULT}/settings`;
export const SETTINGS_PROFILE_META = SETTINGS + '/profile/meta';
export const SETTINGS_PROFILE_UPDATE = SETTINGS + '/profile/update';
export const SETTINGS_PROFILE_UPDATE_COMMON = SETTINGS_PROFILE_UPDATE + '/common';
export const SETTINGS_PROFILE_UPDATE_LK = SETTINGS_PROFILE_UPDATE + '/lk';
export const SETTINGS_PROFILE_UPDATE_PASSWORD = SETTINGS_PROFILE_UPDATE + '/password';
export const SETTINGS_PROFILE_UPDATE_QUESTION = SETTINGS_PROFILE_UPDATE + '/question';
export const SETTINGS_AUTO_REPORTS = SETTINGS + '/autoreports';
export const SETTINGS_STATISTICS = SETTINGS + '/statistics';
export const SETTINGS_LABELS = SETTINGS + '/labels';
export const SETTINGS_POSITIONS = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/positions`;

// Products
export const PRODUCTS = `/${API_VERSION_DEFAULT}/products`;
export const PRODUCTS_GET_CATEGORY_LIST = `${PRODUCTS}/categories`;
export const PRODUCTS_GET_META = `${PRODUCTS}/meta`;
export const PRODUCTS_GET_SERVICES_AVAILABLE = `${PRODUCTS}/list`;
export const PRODUCTS_GET_SERVICES_CONNECTED = `${PRODUCTS}/connected`;
export const PRODUCTS_GET_PRODUCT_OFFERINGS = `${PRODUCTS}/parameters`;

// Promised Payment
export const API_PROMISED_PAYMENT_GET_AMOUNT = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/promised-payment/possibility/{clientId}`;
export const API_PROMISED_PAYMENT_SUBMIT = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/promised-payment/activate/{clientId}`;

// Account Cashback

export const ACCOUNT_CASHBACK_META = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/cashback/meta`;
export const ACCOUNT_CASHBACK_META_WITH_ID = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/cashback/meta/{clientId}`;
export const ACCOUNT_CASHBACK_SEARCH = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/cashback/accounts/search`;

export const ACCOUNT_CASHBACK_PRODUCT_LIST = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/cashback/{clientId}/products/list`;

export const CASHBACK_RESERVE = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/cashback/{clientId}/reserve`;

export const CASHBACK_TOGGLE_AUTO_SPENDING = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/cashback/{clientId}/auto-spending?state={state}`;

export const ACCOUNT_CASHBACK_HISTORY = `/${API_VERSION_DEFAULT}/cashback/history/{clientId}`;
export const ACCOUNT_CASHBACK_HISTORY_LIST = `/${API_VERSION_DEFAULT}/cashback/history/{clientId}`;

// Campaigns

export const API_CAMPAIGN_INFO_GENERAL = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/campaign/{campaignId}/info`;
export const API_CAMPAIGN_INFO_LINK_VISITED = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/campaign/{campaignId}/send-visited-feedback`;
export const API_CAMPAIGN_INFO_ACTIVATE = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/campaign/{campaignId}/activate?fromBasket={fromBasket}`;
export const API_CAMPAIGN_INFO_ACTIVATE_TYPE = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/campaign/{campaignId}/activate/type?fromBasket={fromBasket}`;
export const API_CAMPAIGN_INFO_REMOVE = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/campaign/{campaignId}/remove?fromBasket={fromBasket}`;

// Inquiries
export const API_INQUIRY_ID_PARAM = 'inquiryId';
export const API_INQUIRY_FILE_PARAM = 'fileId';
export const API_INQUIRY_INFO_GENERAL = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/inquiry/{${API_INQUIRY_ID_PARAM}}`;
export const API_INQUIRY_FILE_DOWNLOAD = `${API_INQUIRY_INFO_GENERAL}/attachment/{${API_INQUIRY_FILE_PARAM}}`;
export const API_INQUIRY_CLOSE = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/inquiry/{${API_INQUIRY_ID_PARAM}}/close`;
export const API_INQUIRY_ATTACH_FILE = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/inquiry/attachment`;
export const API_INQUIRY_DETACH_FILE = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/inquiry/attachment/{${API_INQUIRY_FILE_PARAM}}`;
export const API_INQUIRY_ANSWER = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/inquiry/{${API_INQUIRY_ID_PARAM}}/note`;
export const API_INQUIRY_APPROVE = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/inquiry/{${API_INQUIRY_ID_PARAM}}/approve`;
export const API_INQUIRY_REJECT = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/inquiry/{${API_INQUIRY_ID_PARAM}}/reject`;

// AutoReportId
export const API_AUTO_REPORT_ID_PARAM = 'reportId';
export const API_AUTO_REPORT_ID = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/autoreport/{${API_AUTO_REPORT_ID_PARAM}}`;
export const API_AUTO_REPORT_ID_META = `${API_AUTO_REPORT_ID}/meta`;
export const API_AUTO_REPORT_ID_FORMAT_META = `${API_AUTO_REPORT_ID}/format/meta`;
export const API_AUTO_REPORT_ID_FORMAT_UPDATE = `${API_AUTO_REPORT_ID}/format/update`;
export const API_AUTO_REPORT_ID_SCHEDULE_META = `${API_AUTO_REPORT_ID}/schedule/meta`;
export const API_AUTO_REPORT_ID_SCHEDULE_UPDATE = `${API_AUTO_REPORT_ID}/schedule/update`;

const API_AUTO_REPORT_ID_TABLE = API_AUTO_REPORT_ID.replace(`${API_HOSTNAME}`, '');
export const API_AUTO_REPORT_ID_ACCOUNT_TABLE = `${API_AUTO_REPORT_ID_TABLE}/account`;
export const API_AUTO_REPORT_ID_ACCOUNT_TABLE_SAVE = `${API_HOSTNAME}/${API_AUTO_REPORT_ID_ACCOUNT_TABLE}/save`;
export const API_AUTO_REPORT_ID_SUBSCRIBER_TABLE = `${API_AUTO_REPORT_ID_TABLE}/subscriber`;
export const API_AUTO_REPORT_ID_SUBSCRIBER_TABLE_SAVE = `${API_HOSTNAME}/${API_AUTO_REPORT_ID_SUBSCRIBER_TABLE}/save`;

export const API_AUTO_REPORT_ID_ACCOUNT_TABLE_EDIT = `${API_AUTO_REPORT_ID_TABLE}/account-edit`;
export const API_AUTO_REPORT_ID_ACCOUNT_TABLE_EDIT_SAVE = `${API_HOSTNAME}${API_AUTO_REPORT_ID_TABLE}/account-edit/save`;
export const API_AUTO_REPORT_ID_ACCOUNT_TABLE_BASKET_EDIT = `${API_AUTO_REPORT_ID_ACCOUNT_TABLE_EDIT}${BASKET}`;
export const API_AUTO_REPORT_ID_SUBSCRIBER_TABLE_EDIT = `${API_AUTO_REPORT_ID_TABLE}/subscriber-edit`;
export const API_AUTO_REPORT_ID_SUBSCRIBER_TABLE_EDIT_SAVE = `${API_HOSTNAME}${API_AUTO_REPORT_ID_TABLE}/subscriber-edit/save`;
export const API_AUTO_REPORT_ID_SUBSCRIBER_TABLE_BASKET_EDIT = `${API_AUTO_REPORT_ID_SUBSCRIBER_TABLE_EDIT}${BASKET}`;

/*
 * -------------------------------------------
 *        SUBSCRIBER ID
 * -------------------------------------------
 */

export const API_SUBSCRIBER_ID_PARAM = 'subscriberId';
export const API_SUBSCRIBER_ID_FILE_PARAM = 'fileId';
const API_SUBSCRIBER_ID = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/subscriber/{${API_SUBSCRIBER_ID_PARAM}}`;
export const API_SUBSCRIBER_ID_PERSONAL_EDIT_FILE_DOWNLOAD = `${API_SUBSCRIBER_ID}/personal-data/document/{${API_SUBSCRIBER_ID_FILE_PARAM}}`;
export const API_TABLE_SUBSCRIBER_ID = `/${API_VERSION_DEFAULT}/subscriber/{${API_SUBSCRIBER_ID_PARAM}}`;

/*
 * -------------------------------------------
 *        PAYMENTS
 * -------------------------------------------
 */

export const API_PAYMENTS = `/${API_VERSION_DEFAULT}/payments`;
export const API_PAYMENTS_ACCOUNT_ID = `${API_PAYMENTS}/{clientId}`;

export const API_PAYMENTS_EXPORT = `${API_PAYMENTS}/export/{0}`;
export const API_PAYMENTS_ACCOUNT_ID_EXPORT = `${API_PAYMENTS_ACCOUNT_ID}/export/{0}`;

/*
 * -------------------------------------------
 *               ORDERS
 * -------------------------------------------
 */

export const ORDERS = `/${API_VERSION_DEFAULT}/orders`;

// order id

export const API_ORDER_ID = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/order/{orderId}`;

export const API_ORDER_ID_INFO = `${API_ORDER_ID}/meta`;

export const API_ORDER_ID_SUBS_LIST = `${API_ORDER_ID}/list`;

export const API_ORDER_ID_FILE_SEND = `${API_ORDER_ID}/file/send`;
export const API_ORDER_ID_FILE_DOWNLOAD = `${API_ORDER_ID}/file/documents/{fileId}`;

/*
 * -------------------------------------------
 *               ONBOARDING
 * -------------------------------------------
 */

export const API_ONBOARDING = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/onboarding`;

export const API_ONBOARDING_GET_MODAL = `${API_ONBOARDING}/modals/unviewed`;

export const API_ONBOARDING_GET_STATUS = `${API_ONBOARDING}/pages/{id}/info`;

export const API_ONBOARDING_SET_ELEMENT_VIEWED = `${API_ONBOARDING}/elements/{id}/mark-as-viewed`;
export const API_ONBOARDING_SET_MODAL_VIEWED = `${API_ONBOARDING}/modals/mark-as-viewed`;

export const API_ONBOARDING_SHOW_LATER = `${API_ONBOARDING}/postpone`;

export const API_ONBOARDING_ENABLE = `${API_ONBOARDING}/pages/{id}/welcome-onboarding/enable`;
export const API_ONBOARDING_DISABLE = `${API_ONBOARDING}/pages/{id}/welcome-onboarding/disable`;
export const API_ONBOARDING_COMPLETE = `${API_ONBOARDING}/pages/{id}/welcome-onboarding/complete`;

export const API_ONBOARDING_DISABLE_ALL = `${API_ONBOARDING}/welcome-onboarding/disable`;

/*
 * -------------------------------------------
 *           PERSONAL DATA CONFIRM
 * -------------------------------------------
 */

export const API_PERSONAL_DATA_CONFIRM = `${API_HOSTNAME}/${API_VERSION_DEFAULT}/personal-data-confirm`;

export const API_PERSONAL_DATA_CONFIRM_CHECK = `${API_PERSONAL_DATA_CONFIRM}/check`;

/*
 * -------------------------------------------
 *           PERSONAL DATA DELETE
 * -------------------------------------------
 */

export const API_PERSONAL_DATA_DELETE = `${API_HOSTNAME}${SUBSCRIBER_MOBILE}/personal-data-delete`;

/*
 * -------------------------------------------
 *           Edm Delivery
 * -------------------------------------------
 */

const EDM = 'edm';
const OPERATORS = 'operators';
const DICTIONARY = 'dictionary';
const CONNECTIONS = 'connections';
const EDM_OPERATORS = `${EDM}/${OPERATORS}`;
export const EDM_CONNECTIONS = `${EDM}/${CONNECTIONS}`;
const EDM_OPERATORS_DICTIONARY = `${EDM_OPERATORS}/${DICTIONARY}`;
export const EDM_CONNECTIONS_ADD = `${EDM_CONNECTIONS}/${ADD}`;

export const API_EDM_OPERATORS = `${ACCOUNT_INFO}/${EDM_OPERATORS_DICTIONARY}`;

/*
 * -------------------------------------------
 *           Reports Constructor
 * -------------------------------------------
 */

const TEMPLATES = 'templates';
export const REPORTS_DASHBOARD = `${REPORTS}/${DASHBOARD}`;
export const TEMPLATES_DASHBOARD = `${REPORTS_DASHBOARD}/${TEMPLATES}`;
const SUBSCRIBER_EDIT = 'subscriber-edit';
const REPORTS_SUBSCRIBER_EDIT = `${REPORTS_DASHBOARD}/${SUBSCRIBER_EDIT}`;
export const SCHEDULE = 'schedule';
export const SCHEDULE_META = `${SCHEDULE}/${META}`;
export const SCHEDULE_LIST = `${SCHEDULE}/${LIST}`;

export const API_REPORTS_CONSTRUCTOR_META = `${REPORTS_DASHBOARD}/${META}`;

export const API_REPORTS_CONSTRUCTOR_TEMPLATES = `${TEMPLATES_DASHBOARD}/${LIST}`;

export const API_REPORTS_SUBSCRIBER_EDIT_FILTER_AND_SORT = `/${REPORTS_SUBSCRIBER_EDIT}/${FILTER_AND_SORT}`;
export const API_REPORTS_SUBSCRIBER_EDIT_LIST = `/${REPORTS_SUBSCRIBER_EDIT}/${LIST}`;
export const API_REPORTS_SUBSCRIBER_EDIT_META = `/${REPORTS_SUBSCRIBER_EDIT}/${META}`;
export const API_SUBSCRIBERS_LIST_STATUS = `/${SUBSCRIBER}/${MOBILE}/${LIST}/${STATUS}`;

// Subscriber edit basket
export const REPORTS_SUBSCRIBER_EDIT_BASKET = `${REPORTS_DASHBOARD}/${SUBSCRIBER_EDIT}/${BASKET}`;
export const API_REPORTS_SUBSCRIBER_BASKET_CONTAINS = `/${REPORTS_SUBSCRIBER_EDIT_BASKET}/${CONTAINS}`;
export const API_REPORTS_SUBSCRIBER_BASKET_COUNT = `/${REPORTS_SUBSCRIBER_EDIT_BASKET}/${COUNT}`;
export const API_REPORTS_SUBSCRIBER_BASKET_DELETE = `/${REPORTS_SUBSCRIBER_EDIT_BASKET}/${DELETE}`;
export const API_REPORTS_SUBSCRIBER_EDIT_BASKET_LIST = `/${REPORTS_SUBSCRIBER_EDIT_BASKET}/${LIST}`;

/*
 * -------------------------------------------
 *           Collective Product
 * -------------------------------------------
 */

const COLLECTIVE_PRODUCT = 'collective-product';
export const API_COLLECTIVE_PRODUCT_META = `${COLLECTIVE_PRODUCT}/${META}`;

/*
 * -------------------------------------------
 *           User Notifications
 * -------------------------------------------
 */

const NOTIFICATIONS = 'notifications';
export const API_NOTIFICATIONS_SETTINGS = `${NOTIFICATIONS}/settings`;
export const API_NOTIFICATIONS_LIST = `${NOTIFICATIONS}/${LIST}`;
export const API_NOTIFICATIONS_UPDATE = `${NOTIFICATIONS}/${UPDATE}`;
export const API_NOTIFICATIONS_DELETE = `${NOTIFICATIONS}/${DELETE}`;
export const API_NOTIFICATIONS_SHOWN = `${NOTIFICATIONS}/${SHOWN}`;
export const API_NOTIFICATIONS_FEEDBACK = `${NOTIFICATIONS}/${FEEDBACK}`;

/*
 * -------------------------------------------
 *                  News
 * -------------------------------------------
 */

export const API_NEWS = `/${NEWS}`;

export const API_NEWS_ARCHIVE = `${API_NEWS}/${ARCHIVE}`;
export const API_NEWS_ARCHIVE_LIST = `${API_NEWS_ARCHIVE}/${LIST}`;
export const API_NEWS_ARCHIVE_LIST_VIEWED = `${API_NEWS_ARCHIVE_LIST}/${VIEWED}`;

export const API_NEWS_MANGE = `${API_NEWS}/${MANAGE}`;
export const API_NEWS_PUBLISH = `${API_NEWS_MANGE}/${PUBLISH}`;

export const API_NEWS_PUBLISH_EXTERNAL = `${API_NEWS_PUBLISH}/${EXTERNAL}`;
export const API_NEWS_PUBLISH_INTERNAL = `${API_NEWS_PUBLISH}/${INTERNAL}`;

/*
 * -------------------------------------------
 *                  DChat
 * -------------------------------------------
 */

export const TOKEN = 'token';
export const DCHAT = 'dchat';
export const API_DCHAT_TOKEN = `${DCHAT}/${TOKEN}`;
